import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import {
  scanCompletedBack,
  scanCompletedFront,
  scanStartedBack,
  scanStartedFront,
  setCardIdBack,
  setCardIdFront,
  setOcrDataBack,
  setOcrDataFront,
} from '~/actions/addInsurance';
import { imageDataSetBack, imageDataSetFront } from '~/actions/imageData';
import { useSolvSelector } from '~/reducers';
import { CopyImageSagaAction, ImagesSaga, ImageType } from '~/sagas/images';
import { useInsuranceImageUrls } from '.';
import { isAwsLink } from '../util';
import { useEvent } from './useEvent';

const FRONT_PARAMS = {
  imageType: ImageType.HealthInsuranceFront,
  onStart: scanStartedFront,
  onStop: scanCompletedFront,
  setImageData: [imageDataSetFront],
  setImageId: [setCardIdFront],
  setOcrData: setOcrDataFront,
};

const BACK_PARAMS = {
  imageType: ImageType.HealthInsuranceBack,
  onStart: scanStartedBack,
  onStop: scanCompletedBack,
  setImageData: [imageDataSetBack],
  setImageId: [setCardIdBack],
  setOcrData: setOcrDataBack,
};

interface ImageData {
  front?: {
    imageData?: string;
    id?: string;
  };
  back?: {
    imageData?: string;
    id?: string;
  };
}

export interface CheckboxMeta {
  checkboxEnabled: boolean;
  checkboxInitialChecked: boolean;
  checkHasInsuranceFields: () => boolean;
}

export const useCopyInsuranceToggle = (
  bookingId: string
): {
  toggleInsurance: (copy: boolean) => void;
  hasInsuranceToCopy: boolean;
  checkboxMeta: CheckboxMeta;
} => {
  const { insuranceIdFrontId, insuranceIdFrontUrl, insuranceIdBackId, insuranceIdBackUrl } =
    useInsuranceImageUrls({ bookingId });

  const dispatch = useDispatch();
  const dispatchCopy = useEvent((params: any) => {
    dispatch<CopyImageSagaAction>({
      ...params,
      type: ImagesSaga.Copy,
    });
  });

  const client = useQueryClient();

  const imageData = useSolvSelector((state) => state.imageData);
  const insurance = useSolvSelector((state) => state.addInsurance.insurance);
  const paperworkMeta = useSolvSelector((state) => state.paperworkMeta);
  const [initialized, setInitialized] = useState(false);

  const [checkedImages, setCheckedImages] = useState({ front: {}, back: {} } as ImageData);
  const [uncheckedImages, setUncheckedImages] = useState({ front: {}, back: {} } as ImageData);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const [lastSavedChecked, setLastSavedChecked] = useState(false);

  const [hasUploadedInsurance, setHasUploadedInsurance] = useState(false);

  //when the front and back image urls update from aws, refresh their values in state so the image links don't appear broken
  useEffect(() => {
    if (insuranceIdFrontUrl && isAwsLink(insuranceIdFrontUrl) && checkboxChecked) {
      const frontParams = {
        imageUrl: insuranceIdFrontUrl ?? checkedImages.front?.imageData ?? '',
        cardId: insuranceIdFrontId ?? checkedImages.front?.id ?? '',
        ...FRONT_PARAMS,
      };
      setCheckedImages((prevCheckedImages) => ({
        ...prevCheckedImages,
        front: {
          imageData: insuranceIdFrontUrl ?? prevCheckedImages.front?.imageData ?? '',
          id: insuranceIdFrontId ?? prevCheckedImages.front?.id ?? '',
        },
      }));
      dispatchCopy(frontParams);
    }
    if (insuranceIdBackUrl && isAwsLink(insuranceIdBackUrl) && checkboxChecked) {
      const backParams = {
        imageUrl: insuranceIdBackUrl ?? checkedImages.back?.imageData ?? '',
        cardId: insuranceIdBackId ?? checkedImages.back?.id ?? '',
        ...BACK_PARAMS,
      };
      setCheckedImages((prevCheckedImages) => ({
        ...prevCheckedImages,
        back: {
          imageData: insuranceIdBackUrl ?? prevCheckedImages.back?.imageData ?? '',
          id: insuranceIdBackId ?? prevCheckedImages.back?.id ?? '',
        },
      }));
      dispatchCopy(backParams);
    }
  }, [
    insuranceIdFrontUrl,
    insuranceIdBackUrl,
    checkboxChecked,
    insuranceIdFrontId,
    insuranceIdBackId,
    checkedImages.back?.id,
    checkedImages.back?.imageData,
    checkedImages.front?.id,
    checkedImages.front?.imageData,
    dispatchCopy,
  ]);

  //when toggling on/off copy from primary, store the previous state's images to be re-applied when re-toggled
  const setStoredImages = useEvent((checked: boolean) => {
    if (checked) {
      setUncheckedImages({
        front: {
          imageData: imageData?.front ?? '',
          id: insurance?.cardIds?.front ?? '',
        },
        back: {
          imageData: imageData?.back ?? '',
          id: insurance?.cardIds?.back ?? '',
        },
      });
    } else {
      setCheckedImages({
        front: {
          imageData: imageData?.front ?? '',
          id: insurance?.cardIds?.front ?? '',
        },
        back: {
          imageData: imageData?.back ?? '',
          id: insurance?.cardIds?.back ?? '',
        },
      });
    }
  });

  useEffect(() => {
    if (!initialized) {
      if (
        (imageData.front &&
          !isAwsLink(imageData.front) &&
          imageData.front !== paperworkMeta.insuranceId?.frontImgUrl &&
          insurance?.cardIds?.front) ||
        (imageData.back &&
          !isAwsLink(imageData.back) &&
          imageData.back !== paperworkMeta.insuranceId?.backImgUrl &&
          insurance?.cardIds?.back)
      ) {
        setStoredImages(false);
        const frontParams = {
          imageUrl: imageData.front ?? '',
          cardId: insurance?.cardIds.front ?? '',
          ...FRONT_PARAMS,
        };
        const backParams = {
          imageUrl: imageData.back ?? '',
          cardId: insurance?.cardIds.back ?? '',
          ...BACK_PARAMS,
        };
        dispatchCopy(frontParams);
        dispatchCopy(backParams);
        setLastSavedChecked(false);
        setHasUploadedInsurance(true);
      } else {
        setHasUploadedInsurance(false);
      }
      setInitialized(true);
    }
  }, [
    imageData,
    insurance,
    initialized,
    dispatchCopy,
    paperworkMeta.insuranceId?.frontImgUrl,
    paperworkMeta.insuranceId?.backImgUrl,
    setStoredImages,
  ]);

  const toggleInsurance = useEvent((copy: boolean) => {
    setCheckboxChecked(copy);
    if (copy && !lastSavedChecked) {
      setStoredImages(true);
      setLastSavedChecked(true);
    } else if (!copy && lastSavedChecked) {
      setStoredImages(false);
      setLastSavedChecked(false);
    }
    const frontParams = {
      imageUrl:
        (copy
          ? checkedImages.front?.imageData ?? insuranceIdFrontUrl
          : uncheckedImages.front?.imageData) ?? '',
      cardId:
        (copy ? checkedImages.front?.id ?? insuranceIdFrontId : uncheckedImages.front?.id) ?? '',
      ...FRONT_PARAMS,
    };
    const backParams = {
      imageUrl:
        (copy
          ? checkedImages.back?.imageData ?? insuranceIdBackUrl
          : uncheckedImages.back?.imageData) ?? '',
      cardId: (copy ? checkedImages.back?.id ?? insuranceIdBackId : uncheckedImages.back?.id) ?? '',
      ...BACK_PARAMS,
    };
    dispatchCopy(frontParams);
    dispatchCopy(backParams);
    if (copy) {
      client.refetchQueries('get-insurance-id-front');
      client.refetchQueries('get-insurance-id-back');
    }
  });

  const hasInsuranceToCopy = useMemo(() => {
    return !!(
      (insuranceIdFrontUrl && insuranceIdFrontId) ||
      (insuranceIdBackId && insuranceIdBackUrl)
    );
  }, [insuranceIdFrontUrl, insuranceIdFrontId, insuranceIdBackId, insuranceIdBackUrl]);

  const checkHasInsuranceFields = useEvent(() => {
    return !!(
      (imageData.front &&
        !isAwsLink(imageData.front) &&
        imageData.front !== paperworkMeta.insuranceId?.frontImgUrl &&
        insurance?.cardIds?.front) ||
      (imageData.back &&
        !isAwsLink(imageData.back) &&
        imageData.back !== paperworkMeta.insuranceId?.backImgUrl &&
        insurance?.cardIds?.back)
    );
  });

  const checkboxMeta = useMemo(
    () => ({
      checkboxEnabled: hasInsuranceToCopy,
      checkboxInitialChecked: !hasUploadedInsurance,
      checkHasInsuranceFields,
    }),
    [hasInsuranceToCopy, hasUploadedInsurance, checkHasInsuranceFields]
  );
  return {
    toggleInsurance,
    hasInsuranceToCopy,
    checkboxMeta,
  };
};
